import { useInputsWithChoices } from "@/features/dictionaries/useInputsWithChoices";
import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import { parseOptionsFrom, parseShowWhen, type OptionsFromEntry, type PageLogicCondition } from "@patient-watch/forms";
import type { InputT } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { Switch } from "@patient-watch/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";
import {
    LogicConditionFields,
    collectPriorInputs,
    defaultOperator,
    defaultRuleValue,
    type PriorLogicInput,
} from "./LogicConditionFields";

/** Questions on or before `pageId` other than the one being edited, with their choices resolved. */
export function useEarlierQuestions(form: FormBuilderData, pageId: string | null, inputId: string | undefined) {
    const allInputs = useMemo(() => form.pages.flatMap((page) => page.inputs as InputT[]), [form]);
    const withChoices = useInputsWithChoices(allInputs);
    return useMemo(
        () =>
            collectPriorInputs(
                form,
                pageId ?? undefined,
                new Map(withChoices.map((input) => [input.input_id, input]))
            ).filter((input) => input.input_id !== inputId),
        [form, pageId, inputId, withChoices]
    );
}

/** `inputs.show_when`: show this question only when an earlier answer matches. */
export function ShowWhenEditor({
    questions,
    value,
    onChange,
}: {
    questions: PriorLogicInput[];
    value: unknown;
    onChange: (next: PageLogicCondition | null) => void;
}) {
    const condition = parseShowWhen(value);
    const first = questions[0];

    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center gap-2">
                <div>
                    <Label className="text-sm">Show only when</Label>
                    <p className="text-muted-foreground text-xs">Hidden answers are not saved.</p>
                </div>
                <Switch
                    checked={condition != null}
                    disabled={!condition && !first}
                    aria-label="Show this question only when another answer matches"
                    onCheckedChange={(checked) =>
                        onChange(
                            checked && first
                                ? {
                                      input_id: first.input_id,
                                      operator: defaultOperator(first.input_key),
                                      value: defaultRuleValue(first.input_key),
                                  }
                                : null
                        )
                    }
                />
            </div>
            {condition ? (
                <div className="space-y-2 bg-muted/40 p-2 rounded-md">
                    <LogicConditionFields inputs={questions} condition={condition} onChange={onChange} />
                </div>
            ) : null}
        </div>
    );
}

/**
 * `inputs.options_from`: offer only the choices whose codes match ticked checkboxes from earlier,
 * such as "Which of these do you take?" followed by "Which one most?".
 */
export function OptionsFromEditor({
    questions,
    value,
    onChange,
}: {
    questions: PriorLogicInput[];
    value: unknown;
    onChange: (next: OptionsFromEntry[] | null) => void;
}) {
    const entries = parseOptionsFrom(value) ?? [];
    const checkboxes = questions.filter((question) => question.input_key === "checkbox");
    const update = (next: OptionsFromEntry[]) => onChange(next.length ? next : null);

    return (
        <div className="space-y-2">
            <div>
                <Label className="text-sm">Choices from ticked boxes</Label>
                <p className="text-muted-foreground text-xs">
                    Offer only the choices whose code matches a ticked checkbox. Leave empty to offer every choice.
                </p>
            </div>
            {entries.map((entry, index) => (
                <div
                    key={`${entry.input_id}-${index}`}
                    className="items-center gap-2 grid grid-cols-[minmax(0,1fr)_5rem_auto]"
                >
                    <Select
                        value={entry.input_id}
                        onValueChange={(input_id) =>
                            update(entries.map((item, i) => (i === index ? { ...item, input_id } : item)))
                        }
                    >
                        <SelectTrigger size="sm" className="h-8 text-xs">
                            <SelectValue placeholder="Checkbox" />
                        </SelectTrigger>
                        <SelectContent>
                            {checkboxes.map((checkbox) => (
                                <SelectItem key={checkbox.input_id} value={checkbox.input_id}>
                                    {checkbox.pageLabel}: {checkbox.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <ui.Input
                        type="number"
                        aria-label="Choice code"
                        value={entry.code}
                        className="h-8 text-xs"
                        onChange={(event) =>
                            update(
                                entries.map((item, i) =>
                                    i === index ? { ...item, code: Number(event.target.value) || 0 } : item
                                )
                            )
                        }
                    />
                    <ui.Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6"
                        aria-label="Remove"
                        onClick={() => update(entries.filter((_, i) => i !== index))}
                    >
                        <Trash2 size={14} />
                    </ui.Button>
                </div>
            ))}
            <button
                type="button"
                disabled={checkboxes.length === 0}
                className="flex items-center gap-1 hover:bg-muted disabled:opacity-40 px-2 py-1 rounded-md w-full text-muted-foreground hover:text-foreground text-xs disabled:pointer-events-none"
                onClick={() => {
                    const firstBox = checkboxes[0];
                    if (firstBox) update([...entries, { input_id: firstBox.input_id, code: entries.length + 1 }]);
                }}
            >
                <Plus size={14} /> Add checkbox
            </button>
            {checkboxes.length === 0 ? (
                <p className="text-2xs text-muted-foreground">Add checkbox questions before this one to use this.</p>
            ) : null}
        </div>
    );
}
